import { getCompanyList } from "../../../services/api-server/company";
import { getDepartmentsList } from "../../../services/api-server/departments";
import { getMtoCategoriesList } from "../../../services/api-server/mto_categories";
import { getPhases } from "../../../services/api-server/phases";
import { getProjects } from "../../../services/api-server/projects";
import { getTasks } from "../../../services/api-server/tasks";
import { getUsers } from "../../../services/api-server/user";
import { getWells } from "../../../services/api-server/wells";
import { countryList } from "../../countryCodes";
import { LookupConfig } from "../../find_unique_ids_in_list";
import {
	createOperationFetchWithRig,
	fetchAcrossOperatorsByIds,
	fetchAcrossOperatorsNoIds,
} from "./_lookupConfigHelpers";

export function createIncidentLookupConfig(
	operators: string[]
): LookupConfig[] {
	const projectFetch = (ids: any[]) =>
		fetchAcrossOperatorsByIds(
			operators,
			ids,
			(op, ids) =>
				getProjects(op, { _id: { $in: ids } }) as Promise<any[] | null>
		);

	const operationFetch = createOperationFetchWithRig(operators);

	return [
		{
			fetch: (emailList: any[]) => getUsers(emailList) as Promise<any[] | null>,
			sourceKey: "primary_author",
			fallbackSourceKey: "author",
			targetKey: "email",
			outputKey: "author_name",
			transform: (item: any) => item?.name,
		},
		{
			fetch: (emailList: any[]) => getUsers(emailList) as Promise<any[] | null>,
			sourceKey: "reviewer",
			targetKey: "email",
			outputKey: "reviewer_name",
			transform: (item: any) => item?.name,
		},
		{
			fetch: projectFetch,
			sourceKey: "project",
			targetKey: "_id",
			outputKey: "project_name",
			transform: (item: any) => item?.project_name,
		},
		{
			fetch: (ids: any[]) =>
				fetchAcrossOperatorsByIds(
					operators,
					ids,
					(op, ids) =>
						getWells(op, { _id: { $in: ids } }) as Promise<any[] | null>
				),
			sourceKey: "well",
			targetKey: "_id",
			outputKey: "well_name",
			transform: (item: any) => item?.well_name,
		},
		{
			fetch: operationFetch,
			sourceKey: "operation",
			targetKey: "_id",
			outputKey: "operation_name",
			transform: (item: any) => item?.operation_name,
		},
		{
			fetch: operationFetch,
			sourceKey: "operation",
			targetKey: "_id",
			outputKey: "rig",
			transform: (item: any) => item?.rig,
		},
		{
			fetch: operationFetch,
			sourceKey: "operation",
			targetKey: "_id",
			outputKey: "rig_image",
			transform: (item: any) => item?.rig_info?.image,
		},
		{
			fetch: (ids: any[]) =>
				fetchAcrossOperatorsByIds(
					operators,
					ids,
					(op, ids) =>
						getPhases(op, { _id: { $in: ids } }) as Promise<any[] | null>
				),
			sourceKey: "phase",
			targetKey: "_id",
			outputKey: "phase_name",
			transform: (item: any) => item?.phase_name,
		},
		{
			fetch: (ids: any[]) =>
				fetchAcrossOperatorsByIds(
					operators,
					ids,
					(op, ids) =>
						getTasks(op, { _id: { $in: ids } }) as Promise<any[] | null>
				),
			sourceKey: "task",
			targetKey: "_id",
			outputKey: "task_name",
			transform: (item: any) => item?.task_name,
		},
		{
			// ids not used, but `findUniqueIds` calls `fetch(ids)` per `sourceKey`
			fetch: (_ids: any[]) =>
				fetchAcrossOperatorsNoIds(
					operators,
					(op) => getDepartmentsList(op) as Promise<any[] | null>
				),
			sourceKey: "department",
			targetKey: "_id",
			outputKey: "department_name",
			transform: (item: any) => item?.name,
		},
		{
			fetch: projectFetch,
			sourceKey: "project",
			targetKey: "_id",
			outputKey: "project_code",
			transform: (item: any) => item?.project_code,
		},
		{
			// ids not used, but `findUniqueIds` calls `fetch(ids)` per `sourceKey`
			fetch: (_ids: any[]) =>
				fetchAcrossOperatorsNoIds(
					operators,
					(op) => getCompanyList(op) as Promise<any[] | null>
				),
			sourceKey: "companies",
			targetKey: "_id",
			outputKey: "companies_names",
			transform: (item: any) => item?.name,
		},
		{
			// ids not used, but `findUniqueIds` calls `fetch(ids)` per `sourceKey`
			fetch: (_ids: any[]) =>
				fetchAcrossOperatorsNoIds(
					operators,
					(op) => getMtoCategoriesList(op) as Promise<any[] | null>
				),
			sourceKey: "human",
			targetKey: "_id",
			outputKey: "human_category_name",
			transform: (item: any) => item?.name,
		},
		{
			// ids not used, but `findUniqueIds` calls `fetch(ids)` per `sourceKey`
			fetch: (_ids: any[]) =>
				fetchAcrossOperatorsNoIds(
					operators,
					(op) => getMtoCategoriesList(op) as Promise<any[] | null>
				),
			sourceKey: "technical",
			targetKey: "_id",
			outputKey: "technical_category_name",
			transform: (item: any) => item?.name,
		},
		{
			// ids not used, but `findUniqueIds` calls `fetch(ids)` per `sourceKey`
			fetch: (_ids: any[]) =>
				fetchAcrossOperatorsNoIds(
					operators,
					(op) => getMtoCategoriesList(op) as Promise<any[] | null>
				),
			sourceKey: "organisational",
			targetKey: "_id",
			outputKey: "organisational_category_name",
			transform: (item: any) => item?.name,
		},
		{
			fetch: projectFetch,
			sourceKey: "project",
			targetKey: "_id",
			outputKey: "country_name",
			transform: (project: any) => {
				const countryValue = project?.country;
				return countryList.find((c: any) => c["alpha-3"] === countryValue)
					?.name;
			},
		},
		{
			fetch: projectFetch,
			sourceKey: "project",
			targetKey: "_id",
			outputKey: "project_area",
			transform: (item: any) => item?.project_area,
		},
		{
			fetch: (ids: any[]) =>
				fetchAcrossOperatorsByIds(
					operators,
					ids,
					(op, ids) =>
						getMtoCategoriesList(op, { _id: { $in: ids } }) as Promise<
							any[] | null
						>
				),
			sourceKey: "human",
			targetKey: "_id",
			outputKey: "human_category_name",
			transform: (item: any) => item?.name,
		},
		{
			fetch: (ids: any[]) =>
				fetchAcrossOperatorsByIds(
					operators,
					ids,
					(op, ids) =>
						getMtoCategoriesList(op, { _id: { $in: ids } }) as Promise<
							any[] | null
						>
				),
			sourceKey: "technical",
			targetKey: "_id",
			outputKey: "technical_category_name",
			transform: (item: any) => item?.name,
		},
		{
			fetch: (ids: any[]) =>
				fetchAcrossOperatorsByIds(
					operators,
					ids,
					(op, ids) =>
						getMtoCategoriesList(op, { _id: { $in: ids } }) as Promise<
							any[] | null
						>
				),
			sourceKey: "organisational",
			targetKey: "_id",
			outputKey: "organisational_category_name",
			transform: (item: any) => item?.name,
		},
	];
}
